import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Alert, Box, Button, Card, CardContent, Chip, Collapse,
  CircularProgress, Stack, Tab, Tabs, TextField, Typography,
  alpha, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import CategoryIcon from '@mui/icons-material/Category';
import OutboxIcon from '@mui/icons-material/Outbox';
import ReplayIcon from '@mui/icons-material/Replay';
import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';
import {
  approvalsApi, ApprovalItem, ApprovalAction, ApprovalStatus,
} from '@/api/endpoints/approvals';
import { bankDetailsApi, BankDetailsRequestAdminItem } from '@/api/endpoints/hr';
import PageHelpIcon from '@/components/PageHelpIcon';
import { TYPE } from '@/theme/typography';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { usePermission } from '@/hooks/usePermission';
import { usePaletteAccents } from '@/hooks/usePaletteAccents';
import { formatDate, formatDisplayCurrency } from '@/utils/format';

const ENTITY_TYPE_LABELS: Record<string, string> = {
  expense: 'Expense',
  revenue: 'Revenue',
  salary_change: 'Salary Change',
  leave: 'Leave',
};

const STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  changes_requested: 'Needs revision',
};

type ModuleKey = 'accounting' | 'hr' | 'other';

const MODULE_META: Record<ModuleKey, { label: string; Icon: typeof CategoryIcon }> = {
  accounting: { label: 'Accounting', Icon: AccountBalanceIcon },
  hr:         { label: 'HR',         Icon: PeopleIcon },
  other:      { label: 'Other',      Icon: CategoryIcon },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return formatDate(iso);
}

function statusChipColor(status: ApprovalStatus): 'success' | 'error' | 'warning' | 'info' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  if (status === 'changes_requested') return 'info';
  return 'warning';
}

// ─────────────────────────────────────────────────────────────────────────────
// Card used in Pending + History (reviewer perspective)
// ─────────────────────────────────────────────────────────────────────────────
function ApprovalCard({
  item,
  onDecide,
  showActions = true,
}: {
  item: ApprovalItem;
  onDecide?: (id: string, action: ApprovalAction, comments: string) => void;
  showActions?: boolean;
}) {
  const theme = useTheme();
  const accents = usePaletteAccents();
  const { currencySymbol } = useJurisdiction();

  const [reviewOpen, setReviewOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState<string | null>(null);

  const submit = (action: ApprovalAction) => {
    const trimmed = notes.trim();
    if ((action === 'reject' || action === 'request_changes') && trimmed.length < 3) {
      setNotesError(
        action === 'reject'
          ? 'Add a short reason so the requester understands the rejection.'
          : 'Describe what needs to change before they resubmit.',
      );
      return;
    }
    onDecide?.(item.id, action, trimmed);
    setNotes('');
    setNotesError(null);
    setReviewOpen(false);
  };

  const isLeave = item.entity_type === 'leave';
  const amountDisplay = item.entity_amount !== null
    ? (isLeave
        ? `${item.entity_amount} day${item.entity_amount === 1 ? '' : 's'}`
        : formatDisplayCurrency(item.entity_amount, currencySymbol))
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: 'divider',
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': {
          borderColor: alpha(accents.blue, 0.35),
          bgcolor: alpha(accents.blue, 0.02),
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={ENTITY_TYPE_LABELS[item.entity_type] || item.entity_type}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: alpha(accents.blue, 0.4),
                  color: accents.blue,
                  fontWeight: TYPE.semibold,
                }}
              />
              {amountDisplay && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: TYPE.bold, fontSize: TYPE['2xl'], color: 'text.primary' }}
                >
                  {amountDisplay}
                </Typography>
              )}
              <Chip
                label={STATUS_LABELS[item.status] || item.status}
                size="small"
                color={statusChipColor(item.status)}
              />
              <Chip
                label={item.required_role}
                size="small"
                variant="outlined"
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              />
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: TYPE.medium, color: 'text.primary' }}>
              {item.entity_description || 'No description'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Requested by {item.requester_name || 'Unknown'} on {fmtDate(item.created_at)}
              {item.decided_at && ` · decided ${fmtDate(item.decided_at)}`}
            </Typography>
            {item.comments && (
              <Typography
                variant="caption"
                display="block"
                sx={{
                  mt: 0.5, fontStyle: 'italic',
                  color: 'text.secondary',
                  borderLeft: `2px solid ${theme.palette.divider}`,
                  pl: 1,
                }}
              >
                Review: {item.comments}
              </Typography>
            )}
          </Box>

          {showActions && item.status === 'pending' && (
            <Button
              variant={reviewOpen ? 'outlined' : 'contained'}
              color="primary"
              size="small"
              startIcon={<RateReviewIcon />}
              onClick={() => { setReviewOpen((o) => !o); setNotesError(null); }}
              sx={{ flexShrink: 0 }}
            >
              {reviewOpen ? 'Close' : 'Review'}
            </Button>
          )}
        </Stack>

        {showActions && item.status === 'pending' && (
          <Collapse in={reviewOpen} unmountOnExit>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha(accents.blue, 0.04),
                border: `1px solid ${alpha(accents.blue, 0.18)}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  color: accents.blue,
                  fontWeight: TYPE.semibold,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Review notes
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                value={notes}
                onChange={(e) => { setNotes(e.target.value); if (notesError) setNotesError(null); }}
                placeholder="Approve: optional · Reject or Send back: required"
                error={!!notesError}
                helperText={notesError || ' '}
                sx={{ bgcolor: 'background.paper' }}
              />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="flex-end"
                spacing={1}
                sx={{ mt: 1 }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UTurnLeftIcon />}
                  onClick={() => submit('request_changes')}
                  sx={{
                    color: theme.palette.warning.dark,
                    borderColor: alpha(theme.palette.warning.main, 0.5),
                    '&:hover': {
                      borderColor: theme.palette.warning.main,
                      bgcolor: alpha(theme.palette.warning.main, 0.08),
                    },
                  }}
                >
                  Send back for revision
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => submit('reject')}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => submit('approve')}
                >
                  Approve
                </Button>
              </Stack>
            </Box>
          </Collapse>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card used in "My Submissions" (requester perspective) — with Resubmit CTA
// ─────────────────────────────────────────────────────────────────────────────
function SubmissionCard({
  item,
  onResubmit,
}: {
  item: ApprovalItem;
  onResubmit: (id: string) => void;
}) {
  const theme = useTheme();
  const accents = usePaletteAccents();
  const { currencySymbol } = useJurisdiction();

  const isLeave = item.entity_type === 'leave';
  const amountDisplay = item.entity_amount !== null
    ? (isLeave
        ? `${item.entity_amount} day${item.entity_amount === 1 ? '' : 's'}`
        : formatDisplayCurrency(item.entity_amount, currencySymbol))
    : null;

  const canResubmit = item.status === 'changes_requested';

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: canResubmit ? alpha(theme.palette.info.main, 0.4) : 'divider',
        bgcolor: canResubmit ? alpha(theme.palette.info.main, 0.04) : 'background.paper',
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={ENTITY_TYPE_LABELS[item.entity_type] || item.entity_type}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: alpha(accents.blue, 0.4),
                  color: accents.blue,
                  fontWeight: TYPE.semibold,
                }}
              />
              {amountDisplay && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: TYPE.bold, fontSize: TYPE['2xl'], color: 'text.primary' }}
                >
                  {amountDisplay}
                </Typography>
              )}
              <Chip
                label={STATUS_LABELS[item.status] || item.status}
                size="small"
                color={statusChipColor(item.status)}
              />
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: TYPE.medium, color: 'text.primary' }}>
              {item.entity_description || 'No description'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Submitted {fmtDate(item.created_at)}
              {item.decided_at && ` · decided ${fmtDate(item.decided_at)}`}
            </Typography>
            {item.comments && (
              <Typography
                variant="body2"
                display="block"
                sx={{
                  mt: 0.75,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  borderLeft: `3px solid ${theme.palette.info.main}`,
                  color: 'text.primary',
                }}
              >
                <strong>Reviewer says:</strong> {item.comments}
              </Typography>
            )}
          </Box>

          {canResubmit && (
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<ReplayIcon />}
              onClick={() => onResubmit(item.id)}
              sx={{ flexShrink: 0 }}
            >
              Resubmit
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Accordion grouping by module
// ─────────────────────────────────────────────────────────────────────────────
function ModuleAccordion({
  moduleKey,
  children,
  count,
}: {
  moduleKey: ModuleKey;
  children: React.ReactNode;
  count: number;
}) {
  const theme = useTheme();
  const accents = usePaletteAccents();
  const { label, Icon } = MODULE_META[moduleKey];

  return (
    <Accordion
      defaultExpanded={count > 0}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '10px !important',
        overflow: 'hidden',
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: alpha(accents.blue, 0.05),
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&.Mui-expanded': { minHeight: 48 },
          '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1.25 },
        }}
      >
        <Icon sx={{ color: accents.blue, fontSize: 20 }} />
        <Typography sx={{ fontWeight: TYPE.semibold, color: 'text.primary' }}>{label}</Typography>
        <Chip
          label={count}
          size="small"
          color={count > 0 ? 'primary' : 'default'}
          sx={{ fontWeight: TYPE.semibold }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 1.5, bgcolor: 'background.paper' }}>
        {count === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Nothing here yet.
          </Typography>
        ) : children}
      </AccordionDetails>
    </Accordion>
  );
}

function groupByModule(items: ApprovalItem[]): Record<ModuleKey, ApprovalItem[]> {
  const grouped: Record<ModuleKey, ApprovalItem[]> = { accounting: [], hr: [], other: [] };
  for (const it of items) {
    const key = (it.module && grouped[it.module] ? it.module : 'other') as ModuleKey;
    grouped[key].push(it);
  }
  return grouped;
}

// ─────────────────────────────────────────────────────────────────────────────
// Bank-detail change request card (employee-initiated, HR reviews here)
// ─────────────────────────────────────────────────────────────────────────────
function BankRequestCard({
  item,
  onApprove,
  onReject,
  busy,
}: {
  item: BankDetailsRequestAdminItem;
  onApprove: (item: BankDetailsRequestAdminItem) => void;
  onReject: (item: BankDetailsRequestAdminItem, reason: string) => void;
  busy: boolean;
}) {
  const theme = useTheme();
  const accents = usePaletteAccents();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const submitReject = () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setReasonError('Add a short reason so the employee understands the rejection.');
      return;
    }
    onReject(item, trimmed);
    setReason('');
    setReasonError(null);
    setRejectOpen(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: 'divider',
        '&:hover': {
          borderColor: alpha(accents.blue, 0.35),
          bgcolor: alpha(accents.blue, 0.02),
        },
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label="Bank change"
                size="small"
                variant="outlined"
                icon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
                sx={{ borderColor: alpha(accents.blue, 0.4), color: accents.blue, fontWeight: TYPE.semibold }}
              />
              <Typography variant="body2" sx={{ fontWeight: TYPE.semibold, color: 'text.primary' }}>
                {item.employee_name || 'Employee'}
              </Typography>
              {item.designation && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {item.designation}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {item.requested_bank_name || 'Bank'}
              {item.requested_bank_code ? ` (${item.requested_bank_code})` : ''}
              {' · '}
              {item.requested_account_number_masked || '••••'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Requested on {fmtDate(item.created_at)}
            </Typography>
          </Box>

          {item.status === 'pending' && (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                disabled={busy}
                onClick={() => { setRejectOpen((o) => !o); setReasonError(null); }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircleIcon />}
                disabled={busy}
                onClick={() => onApprove(item)}
              >
                Approve
              </Button>
            </Stack>
          )}
        </Stack>

        <Collapse in={rejectOpen} unmountOnExit>
          <Box
            sx={{
              mt: 1.5, p: 1.5, borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.04),
              border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
            }}
          >
            <TextField
              multiline
              rows={2}
              fullWidth
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(null); }}
              placeholder="Reason for rejection (shared with the employee)"
              error={!!reasonError}
              helperText={reasonError || ' '}
              sx={{ bgcolor: 'background.paper' }}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => { setRejectOpen(false); setReasonError(null); }}>
                Cancel
              </Button>
              <Button size="small" variant="contained" color="error" disabled={busy} onClick={submitReject}>
                Confirm rejection
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function MyApprovalsPage({ embedded }: { embedded?: boolean } = {}) {
  const [tab, setTab] = useState(0);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { hasPermission } = usePermission();
  // The bank-change endpoint requires hr.employees.write, but /approvals is
  // reachable by any approver (dashboard.read). Gate the query so non-HR
  // approvers don't hit a spurious 403 → error toast on every page load.
  const canManageBankRequests = hasPermission('hr.employees.write');

  const { data: pendingItems, isLoading: pendingLoading } = useQuery({
    queryKey: ['approvals/pending'],
    queryFn: () => approvalsApi.pending(),
    select: (res) => res.data,
  });

  const { data: historyItems, isLoading: historyLoading } = useQuery({
    queryKey: ['approvals/history'],
    queryFn: () => approvalsApi.history(),
    select: (res) => res.data,
    enabled: tab === 1,
  });

  const { data: submissions, isLoading: submissionsLoading } = useQuery({
    queryKey: ['approvals/my-submissions'],
    queryFn: () => approvalsApi.mySubmissions(),
    select: (res) => res.data,
    enabled: tab === 2,
  });

  // Employee bank-detail change requests (separate table + endpoint from the
  // generic approvals framework — surfaced here so HR has an Approve/Reject CTA).
  const { data: bankRequests, isLoading: bankLoading } = useQuery({
    queryKey: ['bank-details-requests/pending'],
    queryFn: () => bankDetailsApi.listRequests('pending'),
    enabled: canManageBankRequests,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['approvals/pending'] });
    queryClient.invalidateQueries({ queryKey: ['bank-details-requests/pending'] });
    queryClient.invalidateQueries({ queryKey: ['approvals/history'] });
    queryClient.invalidateQueries({ queryKey: ['approvals/my-submissions'] });
    queryClient.invalidateQueries({ queryKey: ['approvals/stats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard/action-center'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const decideMutation = useMutation({
    mutationFn: ({ id, action, comments }: { id: string; action: ApprovalAction; comments: string }) =>
      approvalsApi.decide(id, action, comments),
    onSuccess: invalidateAll,
  });

  const resubmitMutation = useMutation({
    mutationFn: (id: string) => approvalsApi.resubmit(id),
    onSuccess: invalidateAll,
  });

  const bankReviewMutation = useMutation({
    mutationFn: ({ item, decision, reason }: {
      item: BankDetailsRequestAdminItem;
      decision: 'approve' | 'reject';
      reason?: string;
    }) => bankDetailsApi.review(item.employee_id, item.id, decision, reason),
    onSuccess: invalidateAll,
  });

  const handleBankApprove = (item: BankDetailsRequestAdminItem) =>
    bankReviewMutation.mutate({ item, decision: 'approve' });
  const handleBankReject = (item: BankDetailsRequestAdminItem, reason: string) =>
    bankReviewMutation.mutate({ item, decision: 'reject', reason });

  const handleDecide = (id: string, action: ApprovalAction, comments: string) => {
    decideMutation.mutate({ id, action, comments });
  };
  const handleResubmit = (id: string) => {
    resubmitMutation.mutate(id);
  };

  const pendingGroups    = useMemo(() => groupByModule(pendingItems    ?? []), [pendingItems]);
  const historyGroups    = useMemo(() => groupByModule(historyItems    ?? []), [historyItems]);
  const submissionGroups = useMemo(() => groupByModule(submissions     ?? []), [submissions]);

  const totalPending = pendingItems?.length ?? 0;
  const bankPending = bankRequests ?? [];
  const combinedPending = totalPending + bankPending.length;
  const revisionCount = (submissions ?? []).filter((s) => s.status === 'changes_requested').length;

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
            <Typography variant="h4">My Approvals</Typography>
            <PageHelpIcon pageKey="approvals" />
            {combinedPending > 0 && (
              <Chip
                label={combinedPending}
                color="error"
                size="small"
                sx={{ fontWeight: TYPE.bold, ml: 1 }}
              />
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Review decisions owed to you, see your past rulings, and track your own submissions.
          </Typography>
        </>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: TYPE.semibold },
        }}
      >
        <Tab
          icon={<PendingActionsIcon />}
          iconPosition="start"
          label={`Pending${pendingItems ? ` (${combinedPending})` : ''}`}
        />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
        <Tab
          icon={<OutboxIcon />}
          iconPosition="start"
          label={`My Submissions${revisionCount ? ` (${revisionCount})` : ''}`}
        />
      </Tabs>

      {(decideMutation.isError || resubmitMutation.isError || bankReviewMutation.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(
            (bankReviewMutation.error as any)?.response?.data?.detail ===
              'active_payroll_in_progress'
              ? 'A payroll run is in progress — bank changes are locked until it finishes.'
              : (decideMutation.error as any)?.response?.data?.detail ||
                (resubmitMutation.error as any)?.response?.data?.detail ||
                (bankReviewMutation.error as any)?.response?.data?.detail ||
                'Action failed. Please try again.',
          )}
        </Alert>
      )}

      {/* ── Pending ── */}
      {tab === 0 && (
        (pendingLoading || bankLoading) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : combinedPending === 0 ? (
          <Card variant="outlined" sx={{ borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: TYPE.semibold }}>You're all caught up</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                New approval requests will appear here as they are submitted.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {bankPending.length > 0 && (
              <ModuleAccordion moduleKey="hr" count={bankPending.length}>
                {bankPending.map((req) => (
                  <BankRequestCard
                    key={req.id}
                    item={req}
                    onApprove={handleBankApprove}
                    onReject={handleBankReject}
                    busy={bankReviewMutation.isPending}
                  />
                ))}
              </ModuleAccordion>
            )}
            {(['accounting', 'hr', 'other'] as ModuleKey[])
              .filter((m) => pendingGroups[m].length > 0)
              .map((m) => (
                <ModuleAccordion key={m} moduleKey={m} count={pendingGroups[m].length}>
                  {pendingGroups[m].map((item) => (
                    <ApprovalCard key={item.id} item={item} onDecide={handleDecide} />
                  ))}
                </ModuleAccordion>
              ))}
          </>
        )
      )}

      {/* ── History ── */}
      {tab === 1 && (
        historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (historyItems?.length ?? 0) === 0 ? (
          <Card variant="outlined" sx={{ borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: TYPE.semibold }}>No approval history</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Your past approval decisions will appear here.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          (['accounting', 'hr', 'other'] as ModuleKey[])
            .filter((m) => historyGroups[m].length > 0)
            .map((m) => (
              <ModuleAccordion key={m} moduleKey={m} count={historyGroups[m].length}>
                {historyGroups[m].map((item) => (
                  <ApprovalCard key={item.id} item={item} showActions={false} />
                ))}
              </ModuleAccordion>
            ))
        )
      )}

      {/* ── My Submissions ── */}
      {tab === 2 && (
        submissionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (submissions?.length ?? 0) === 0 ? (
          <Card variant="outlined" sx={{ borderColor: 'divider' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <OutboxIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: TYPE.semibold }}>No submissions yet</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Requests you raise that need approval will show up here with their status.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          (['accounting', 'hr', 'other'] as ModuleKey[])
            .filter((m) => submissionGroups[m].length > 0)
            .map((m) => (
              <ModuleAccordion key={m} moduleKey={m} count={submissionGroups[m].length}>
                {submissionGroups[m].map((item) => (
                  <SubmissionCard key={item.id} item={item} onResubmit={handleResubmit} />
                ))}
              </ModuleAccordion>
            ))
        )
      )}
    </Box>
  );
}
